import block from 'propmods';
import React from 'react';
import {
    InstallableApp,
    AliceSkill,
    Channel,
    AliceApp,
    SmartHome,
    SmartScreenApp,
    ThereminvoxSkill,
    TvApp,
    AutoApp,
} from '../../model/skill';
import AliceSkillForm from './channels/AliceSkillForm';
import SmartHomeForm from './channels/SmartHomeForm';
import InstallableAppForm from '../../blocks-redesign/Settings/channels/InstallableAppForm';
import { ThereminvoxForm } from './channels/ThereminvoxForm';
import {
    ThereminvoxSettings,
    SmartHomeSettings,
    InstallableAppSettings,
    AliceSkillSettings,
} from '../../model/settings';
import { isInDevelopment } from '../../model/draft';

interface SettingsProps {
    aliceApp: AliceApp;
    isDraft: boolean;
    hasChanges: boolean;
    registerChanges: (hasChanges: boolean) => void;
    settings: AliceApp['draft'] | AliceApp;
}

const b = block('Settings');

export default class Settings extends React.Component<SettingsProps> {
    private get isEditable() {
        const { isDraft, aliceApp } = this.props;

        return isDraft && isInDevelopment(aliceApp);
    }

    public render() {
        return <div {...b(this)}>{this.renderSettings()}</div>;
    }

    private renderSettings = () => {
        const { aliceApp } = this.props;

        switch (aliceApp.channel) {
            case Channel.AliceSkill:
                return this.renderSkillSettings(aliceApp as AliceSkill);
            case Channel.SmartHome:
                return this.renderSmartHomeSettings(aliceApp as SmartHome);
            case Channel.Thereminvox:
                return this.renderThereminvoxSettings(aliceApp as ThereminvoxSkill);
            case Channel.AutoApp:
                return this.renderAppSettings(aliceApp as AutoApp);
            case Channel.TvApp:
                return this.renderAppSettings(aliceApp as TvApp);
            case Channel.SmartScreenApp:
                return this.renderAppSettings(aliceApp as SmartScreenApp);
        }
    };

    private renderAppSettings = (app: InstallableApp) => {
        const { isDraft, settings, registerChanges, hasChanges } = this.props;
        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${app.draft.status}`;

        return (
            <InstallableAppForm
                key={`TvAppForm${draftModifier}${draftStatusModifier}`}
                skillUseZora={app.useZora !== undefined ? app.useZora : true}
                logoUrl={isDraft ? app.draft.logoUrl : app.logoUrl}
                settings={settings as InstallableAppSettings}
                disabled={!this.isEditable}
                isDraft={Boolean(this.props.isDraft)}
                registerChanges={registerChanges}
                hasChanges={hasChanges}
                app={app}
            />
        );
    };

    private renderSkillSettings = (skill: AliceSkill) => {
        const { isDraft, registerChanges, hasChanges } = this.props;
        const settings = this.props.settings as AliceSkillSettings;
        const isAccountLinkingTemplate = Boolean(
            isDraft && this.props.aliceApp.oauthAppId === this.props.aliceApp.draft.oauthAppId,
        );

        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${skill.draft.status}`;

        const displayEmailVerificationError =
            Boolean(settings.publishingSettings.email) && !settings.publishingSettings.emailIsVerified;

        const displayBrandVerificationError =
            Boolean(settings.publishingSettings.brandVerificationWebsite) &&
            !settings.publishingSettings.brandIsVerified;

        const brandIsVerified = Boolean(settings.publishingSettings.brandIsVerified);

        return (
            <AliceSkillForm
                key={`AliceSkillForm${draftModifier}${draftStatusModifier}`}
                skillUseZora={skill.useZora !== undefined ? skill.useZora : true}
                logoUrl={settings.logo2 ? settings.logo2.url : ''}
                logoColor={settings.logo2 ? settings.logo2.meta.MainColor : ''}
                settings={settings}
                displayBrandVerificationError={displayBrandVerificationError}
                brandIsVerified={brandIsVerified}
                displayEmailVerificationError={displayEmailVerificationError}
                disabled={!this.isEditable}
                isDraft={Boolean(this.props.isDraft)}
                registerChanges={registerChanges}
                hasChanges={hasChanges}
                isAccountLinkingTemplate={isAccountLinkingTemplate}
                skill={skill}
            />
        );
    };

    private renderSmartHomeSettings = (skill: SmartHome) => {
        const { isDraft, registerChanges, settings, hasChanges } = this.props;
        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${skill.draft.status}`;

        const displayEmailVerificationError =
            Boolean(settings.publishingSettings.email) && !settings.publishingSettings.emailIsVerified;

        const displayBrandVerificationError =
            Boolean(settings.publishingSettings.brandVerificationWebsite) &&
            !settings.publishingSettings.brandIsVerified;

        const isAccountLinkingTemplate = Boolean(
            isDraft && this.props.aliceApp.oauthAppId === this.props.aliceApp.draft.oauthAppId,
        );

        return (
            <SmartHomeForm
                key={`SmartHomeForm${draftModifier}${draftStatusModifier}`}
                skillUseZora={skill.useZora !== undefined ? skill.useZora : true}
                logoUrl={settings.logo2 ? settings.logo2.url : ''}
                logoColor={settings.logo2 ? settings.logo2.meta.MainColor : ''}
                settings={settings as SmartHomeSettings}
                displayBrandVerificationError={displayBrandVerificationError}
                displayEmailVerificationError={displayEmailVerificationError}
                disabled={!this.isEditable}
                isDraft={Boolean(this.props.isDraft)}
                registerChanges={registerChanges}
                hasChanges={hasChanges}
                isAccountLinkingTemplate={isAccountLinkingTemplate}
                skill={skill}
            />
        );
    };

    private renderThereminvoxSettings = (skill: ThereminvoxSkill) => {
        const { isDraft, registerChanges, settings, hasChanges } = this.props;
        const draftModifier = isDraft ? '_draft' : '';
        const draftStatusModifier = `_status_${skill.draft.status}`;

        return (
            <ThereminvoxForm
                key={`ThereminvoxForm${draftModifier}${draftStatusModifier}`}
                settings={settings as ThereminvoxSettings}
                disabled={!this.isEditable}
                hasChanges={hasChanges}
                registerChanges={registerChanges}
                skill={skill}
            />
        );
    };
}
